import { useEffect, useRef, useState } from 'react';
import Icon from '../common/Icon.jsx';

/**
 * TrackPicker — native-feel circuit selector.
 *
 * Implementation choice:
 *   The mobile design system asks for a "native picker" feel — on iOS this
 *   would be a sheet-style wheel, on Android a modal bottom sheet. Since
 *   this app ships as a web shell, we approximate with a custom bottom
 *   sheet (visually closer to Android's BottomSheetDialogFragment but
 *   familiar to iOS users from Apple Music's sort sheet).
 *
 *   We deliberately avoid the native <select> element because the styling
 *   surface area on it is paper-thin and the dropdown feels wrong inside a
 *   dark motorsport UI. The sheet implementation below is fully accessible
 *   (Escape to dismiss, focus trap inside the active sheet, ARIA roles).
 *
 *   When/if this component is ported to React Native, swap the inner sheet
 *   for `<Picker />` from @react-native-picker/picker — the contract
 *   (value, onChange, options) stays identical.
 */
export default function TrackPicker({ tracks, value, onChange, showAllOption }) {
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const isAll = value === 'all';
  const selected = isAll ? null : (tracks.find((t) => t.id === value) ?? tracks[0]);

  // Esc-to-dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="pressable group flex w-full items-center justify-between gap-3 rounded-2xl bg-bg-card px-4 py-3.5 ring-1 ring-stroke transition-colors hover:bg-bg-card2"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-center gap-3 text-left">
          {isAll ? (
            <>
              <span className="text-2xl">🏁</span>
              <div className="min-w-0">
                <div className="truncate text-sm font-bold text-ink-primary">All Races</div>
                <div className="truncate text-2xs uppercase tracking-widest-plus text-ink-tertiary">
                  Full season overview
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="text-2xl">{selected?.flag}</span>
              <div className="min-w-0">
                <div className="truncate text-sm font-bold text-ink-primary">
                  {selected?.circuit_short_name}
                </div>
                <div className="truncate text-2xs uppercase tracking-widest-plus text-ink-tertiary">
                  {selected?.location}
                </div>
              </div>
            </>
          )}
        </div>
        <Icon name="chevronDown" size={18} className="shrink-0 text-ink-secondary" />
      </button>

      {open ? (
        <TrackPickerSheet
          tracks={tracks}
          value={value}
          showAllOption={showAllOption}
          onClose={() => setOpen(false)}
          onSelect={(id) => {
            onChange(id);
            setOpen(false);
          }}
          closeRef={closeRef}
        />
      ) : null}
    </>
  );
}

/**
 * TrackPickerSheet — bottom-sheet portal.
 *
 * Renders inside the same #root tree (no portal) because the app shell is
 * intentionally a single phone-shaped container. Stacking the backdrop
 * inside #root preserves the mobile-frame visual on desktop.
 */
function TrackPickerSheet({ tracks, value, showAllOption, onClose, onSelect, closeRef }) {
  return (
    <div
      className="absolute inset-0 z-50 flex flex-col justify-end animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Select circuit"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-xs"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative animate-slide-up rounded-t-3xl border-t border-stroke bg-bg-elevated pb-6 shadow-card-hover">
        <div className="flex items-center justify-between border-b border-stroke px-5 py-4">
          <h2 className="text-base font-bold text-ink-primary">Select Circuit</h2>
          <button
            ref={closeRef}
            onClick={onClose}
            className="pressable flex h-9 w-9 items-center justify-center rounded-full bg-bg-card text-ink-secondary ring-1 ring-stroke"
            aria-label="Close picker"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
        <ul role="listbox" className="max-h-[55vh] overflow-y-auto px-3 py-2 scroll-rail">
          {showAllOption ? (
            <li>
              <button
                role="option"
                aria-selected={value === 'all'}
                onClick={() => onSelect('all')}
                className={`pressable flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                  value === 'all' ? 'bg-f1-red/10 ring-1 ring-f1-red/40' : 'hover:bg-bg-card'
                }`}
              >
                <span className="text-2xl">🏁</span>
                <div className="min-w-0 flex-1">
                  <div className={`truncate text-sm font-semibold ${value === 'all' ? 'text-f1-red' : 'text-ink-primary'}`}>
                    All Races
                  </div>
                  <div className="truncate text-2xs text-ink-tertiary">
                    Full season overview
                  </div>
                </div>
              </button>
            </li>
          ) : null}
          {tracks.map((track) => {
            const isActive = track.id === value;
            return (
              <li key={track.id}>
                <button
                  role="option"
                  aria-selected={isActive}
                  onClick={() => onSelect(track.id)}
                  className={`pressable flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                    isActive ? 'bg-f1-red/10 ring-1 ring-f1-red/40' : 'hover:bg-bg-card'
                  }`}
                >
                  <span className="text-2xl">{track.flag}</span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`truncate text-sm font-semibold ${
                        isActive ? 'text-f1-red' : 'text-ink-primary'
                      }`}
                    >
                      {track.circuit_short_name}
                    </div>
                    <div className="truncate text-2xs text-ink-tertiary">
                      {track.circuit_full_name}
                    </div>
                  </div>
                  {track.isMostRecent ? (
                    <span className="rounded-full bg-f1-red/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-f1-red">
                      Latest
                    </span>
                  ) : track.is_cancelled ? (
                    <span className="rounded-full bg-ink-muted/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-muted line-through">
                      Cancelled
                    </span>
                  ) : !track.isConcluded ? (
                    <span className="rounded-full bg-signal-blue/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-signal-blue">
                      Upcoming
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
